import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  Divider,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, Lock as LockIcon, Email as EmailIcon, Person as PersonIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Profil bilgileri
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState('');
  
  // Şifre değiştirme
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      setUsername(response.data.username || '');
      setEmail(response.data.email || '');
    } catch (err: any) {
      setError('Profil bilgileri yüklenemedi');
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.put('/api/auth/profile', {
        username,
        email
      });
      setSuccess('✅ Profil bilgileri güncellendi!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Şifre kontrolü
    if (newPassword !== confirmPassword) {
      setError('Yeni şifreler eşleşmiyor!');
      return;
    }

    if (newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır!');
      return;
    }

    setLoading(true);

    try {
      await axios.put('/api/auth/password', {
        currentPassword,
        newPassword
      });
      setSuccess('✅ Şifre başarıyla güncellendi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şifre güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profil Ayarları
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Kullanıcı bilgilerinizi ve şifrenizi buradan güncelleyebilirsiniz
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profil Bilgileri */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Profil Bilgileri</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <form onSubmit={handleUpdateProfile}>
                <TextField
                  fullWidth
                  label="Kullanıcı Adı"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <TextField
                  fullWidth
                  label="E-posta"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Kaydediliyor...' : 'Bilgileri Güncelle'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Şifre Değiştirme */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Şifre Değiştir</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <form onSubmit={handleUpdatePassword}>
                <TextField
                  fullWidth
                  label="Mevcut Şifre"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <TextField
                  fullWidth
                  label="Yeni Şifre"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  helperText="En az 6 karakter"
                />
                <TextField
                  fullWidth
                  label="Yeni Şifre (Tekrar)"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Kullanıcı Bilgi Kartı */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hesap Bilgileri
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Kullanıcı Adı:</Typography>
                  <Typography variant="h6">{user?.username}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>E-posta:</Typography>
                  <Typography variant="h6">{email || 'Henüz girilmedi'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Rol:</Typography>
                  <Typography variant="h6">{user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;

